// specs/018-ai-deal-form-assistant — SKU screen behaviour (FR-033 – FR-044). jQuery, because the screen
// lives inside the legacy SmartWizard and shares its whole-form serialisation (research D11).
import {initSelect2OnElement} from '../select2';

const fill = (template, values) => String(template || '').replace(/%\{(\w+)\}/g, (_, key) => values[key] ?? '');

window.SkuCards = {
    init($screen) {
        if (!$screen || !$screen.length) return;
        this.$screen = $screen;
        this.$list = $screen.find('.sku-screen__list');
        this.maxSkus = parseInt($screen.data('max-skus'), 10) || 8;
        this.labels = $screen.data('labels') || {};
        this.periods = $screen.data('periods') || {};
        this.nextIndex = this.cards().length;

        this.cards().each((_, card) => this.setupCard($(card)));
        this.bindEvents();
        this.refresh();
    },

    cards() {
        return this.$list.children('.sku-card').not('.sku-card--removed');
    },

    bindEvents() {
        const self = this;
        this.$list.on('click', '.sku-card__header', function (e) {
            if ($(e.target).closest('.sku-card__remove').length) return;
            $(this).closest('.sku-card').toggleClass('sku-card--expanded');
        });
        this.$list.on('keydown', '.sku-card__header', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                $(this).closest('.sku-card').toggleClass('sku-card--expanded');
            }
        });
        this.$list.on('click', '.sku-card__collapse', function () {
            $(this).closest('.sku-card').removeClass('sku-card--expanded');
        });
        this.$list.on('click', '.sku-card__remove', function (e) {
            e.stopPropagation(); // never toggles the card (FR-036)
            self.removeCard($(this).closest('.sku-card'));
        });
        this.$list.on('input change', '.sku-card input, .sku-card select', function () {
            const $card = $(this).closest('.sku-card');
            $(this).removeClass('is-invalid');
            self.updateCard($card);
        });
        // Scrolling over a focused number input would change its value; drop focus so the page scrolls instead.
        this.$list.on('wheel', '.sku-card input[type=number]', function () {
            if (document.activeElement === this) this.blur();
        });
        this.$screen.on('click', '.sku-screen__add', () => this.addCard());
    },

    setupCard($card) {
        $card.find('select.sku-card__multi').each((_, el) => {
            if (!$(el).hasClass('select2-hidden-accessible')) initSelect2OnElement(el, {width: '100%'});
        });
        this.updateCard($card);
    },

    // Header preview, summary pills and Qty per Period (FR-034, FR-040, FR-041).
    updateCard($card) {
        const $screen = this.$screen;
        const details = ($card.find('.sku-card__details').val() || '').trim();
        const quantity = parseInt($card.find('.sku-card__quantity').val(), 10);
        const frequency = $card.find('.sku-card__frequency').val();
        const cost = parseFloat($card.find('.sku-card__cost').val());

        const $title = $card.find('.sku-card__title');
        const number = this.cards().index($card) + 1;
        $card.find('.sku-card__index').text(number);
        if (details) {
            $title.text(details.length > 52 ? `${details.slice(0, 52)}...` : details).attr('data-empty', 'false');
        } else {
            $title.text(fill($screen.data('placeholder-title'), {number})).attr('data-empty', 'true');
        }

        const $qtyPill = $card.find('.sku-card__pill--quantity');
        if (quantity >= 0 && !Number.isNaN(quantity)) {
            $qtyPill.text(fill($screen.data('per-year'), {quantity: quantity.toLocaleString()})).removeClass('d-none');
        } else {
            $qtyPill.addClass('d-none');
        }
        const $costPill = $card.find('.sku-card__pill--cost');
        if (cost >= 0 && !Number.isNaN(cost)) {
            $costPill.text(fill($screen.data('cost-pill'), {price: `${$screen.data('currency')}${cost.toFixed(2)}`}))
                .removeClass('d-none');
        } else {
            $costPill.addClass('d-none');
        }

        // Same rule as the classic form and Deal#quantity_per_period: whole units, rounded up.
        $card.find('.sku-card__period-label').text(this.labels[frequency] || this.labels.blank);
        const $period = $card.find('.sku-card__period');
        if (frequency && quantity > 0 && this.periods[frequency]) {
            $period.text(Math.ceil(quantity / this.periods[frequency]).toLocaleString())
                .removeClass('sku-card__period--empty');
        } else {
            $period.text($screen.data('calculated')).addClass('sku-card__period--empty');
        }
    },

    // "+ Add SKU": copies Product Details, Labelling, Product Level and Frequency from the last card (FR-043).
    addCard() {
        if (this.cards().length >= this.maxSkus) return;
        const html = this.$screen.find('template.sku-screen__template').html().replace(/__INDEX__/g, this.nextIndex);
        this.nextIndex += 1;
        const $card = $(html);
        const $last = this.cards().last();
        if ($last.length) {
            $card.find('.sku-card__details').val($last.find('.sku-card__details').val());
            $card.find('.sku-card__frequency').val($last.find('.sku-card__frequency').val());
            $card.find('.sku-card__unit').val($last.find('.sku-card__unit').val());
            $card.find('.sku-card__labelling').val($last.find('.sku-card__labelling').val());
            $card.find('.sku-card__levels').val($last.find('.sku-card__levels').val());
        }
        $card.addClass('sku-card--expanded');
        this.$list.append($card);
        this.setupCard($card);
        this.refresh();
        $card.find('.sku-card__quantity').trigger('focus');
    },

    // ×: saved cards are marked for destruction and hidden; unsaved ones are dropped (FR-036).
    removeCard($card) {
        if (this.cards().length <= 1) return;
        if ($card.find('.sku-card__id').val()) {
            $card.find('.sku-card__destroy').val('1');
            $card.addClass('sku-card--removed d-none');
        } else {
            $card.remove();
        }
        this.refresh();
    },

    refresh() {
        const $cards = this.cards();
        const count = $cards.length;
        const countText = count === 1 ? this.$screen.data('item-count-one')
            : fill(this.$screen.data('item-count-other'), {count});
        this.$screen.find('.sku-screen__count').text(countText);
        this.$screen.toggleClass('sku-screen--single', count <= 1);
        const full = count >= this.maxSkus;
        this.$screen.find('.sku-screen__add').prop('disabled', full);
        this.$screen.find('.sku-screen__max-hint').toggleClass('d-none', !full);
        $cards.each((index, card) => {
            $(card).find('.sku-card__position').val(index);
            $(card).find('.sku-card__index').text(index + 1);
        });
    },

    // Required per SKU: Product Details, Annual Quantity (whole number ≥ 0), one Labelling (FR-044).
    validate() {
        let firstInvalid = null;
        this.cards().each((_, card) => {
            const $card = $(card);
            const checks = [
                [$card.find('.sku-card__details'), (v) => v.trim().length > 0],
                [$card.find('.sku-card__quantity'), (v) => /^\d+$/.test(v.trim())],
                [$card.find('.sku-card__labelling'), () => ($card.find('.sku-card__labelling').val() || []).length > 0],
            ];
            checks.forEach(([$field, ok]) => {
                const valid = ok(String($field.val() || ''));
                $field.toggleClass('is-invalid', !valid);
                $field.next('.select2').toggleClass('is-invalid', !valid);
                if (!valid && !firstInvalid) firstInvalid = $card;
            });
        });
        if (firstInvalid) {
            firstInvalid.addClass('sku-card--expanded');
            firstInvalid[0].scrollIntoView({behavior: 'smooth', block: 'center'});
            return false;
        }
        return true;
    },

    // Saves send the whole form, so new cards must pick up their ids before the next save.
    applySavedIds(skus) {
        if (!Array.isArray(skus) || !this.$list) return;
        this.$list.children('.sku-card--removed').remove();
        this.cards().each((index, card) => {
            const saved = skus.find((sku) => sku.position === index);
            if (saved && saved.id) $(card).find('.sku-card__id').val(saved.id);
        });
    },
};
